import React from "react";
import { ArrowRight, Phone, MapPin, Star } from "lucide-react";
import { scrollToQuote } from "@/lib/scrollToQuote";

export default function Hero({ hero, contact }) {
    return (
        <section id="top" className="relative min-h-screen w-full overflow-hidden bg-[#050505] pt-20">
            {/* Background image + scrim */}
            <div className="absolute inset-0 z-0">
                <img
                    src={hero.background_url}
                    alt=""
                    className="w-full h-full object-cover opacity-30"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black via-black/85 to-black/60" />
                <div className="absolute inset-0 stripe-bg opacity-30" />
            </div>

            {/* Kinetic marquee background text */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 pointer-events-none overflow-hidden z-0">
                <div className="marquee flex whitespace-nowrap font-display text-[18vw] text-white/[0.03]">
                    <span className="pr-16">RUIZ&nbsp;CONCRETE&nbsp;·&nbsp;COLORADO&nbsp;SPRINGS&nbsp;·&nbsp;</span>
                    <span className="pr-16">RUIZ&nbsp;CONCRETE&nbsp;·&nbsp;COLORADO&nbsp;SPRINGS&nbsp;·&nbsp;</span>
                </div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-24 pb-16 md:pb-24">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    {/* LEFT — copy */}
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <div className="rise-in flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-yellow-400" />
                            <span className="font-mono-accent text-[11px] text-yellow-400">
                                {hero.eyebrow}
                            </span>
                        </div>

                        <h1
                            data-testid="hero-headline"
                            className="rise-in rise-in-delay-1 font-display text-white text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] tracking-tight"
                        >
                            {hero.headline_top}
                            <br />
                            <span className="text-yellow-400">{hero.headline_accent}.</span>
                        </h1>

                        <p className="rise-in rise-in-delay-2 mt-8 max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed">
                            {hero.subheadline}
                        </p>

                        <div className="rise-in rise-in-delay-3 mt-10 flex flex-col sm:flex-row gap-4">
                            <a
                                href="#quote"
                                onClick={scrollToQuote}
                                data-testid="hero-cta-primary"
                                className="group bg-yellow-400 text-black font-black uppercase tracking-wider px-8 py-5 text-sm hover:bg-yellow-300 transition-colors inline-flex items-center gap-3 justify-center"
                            >
                                {hero.cta_primary}
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                            <a
                                href={`tel:${contact.phone?.replace(/[^\d+]/g, "")}`}
                                data-testid="hero-cta-secondary"
                                className="border-2 border-zinc-700 text-white font-bold uppercase tracking-wider px-8 py-5 text-sm hover:border-yellow-400 hover:text-yellow-400 transition-colors inline-flex items-center gap-3 justify-center"
                            >
                                <Phone className="w-4 h-4" /> {hero.cta_secondary}
                            </a>
                        </div>

                        {/* Trust row */}
                        <div className="rise-in rise-in-delay-4 mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
                            <div className="flex items-center gap-2">
                                <div className="flex">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                    ))}
                                </div>
                                <span className="font-mono-accent text-[11px] text-zinc-400">
                                    5.0 · Local Reviews
                                </span>
                            </div>
                            <div className="flex items-center gap-2 font-mono-accent text-[11px] text-zinc-400">
                                <MapPin className="w-3.5 h-3.5 text-yellow-400" /> Serving {contact.service_area}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — logo mount */}
                    <div className="lg:col-span-5 order-1 lg:order-2 relative">
                        <div className="relative aspect-square max-w-md mx-auto">
                            {/* Diagonal accent frame */}
                            <div className="absolute -inset-4 border border-zinc-800 -rotate-3" />
                            <div className="absolute -inset-2 border border-yellow-400/40 rotate-2" />

                            {/* Logo */}
                            <div className="relative w-full h-full flex items-center justify-center p-4">
                                <img
                                    data-testid="hero-logo"
                                    src={hero.logo_url}
                                    alt="Ruiz Concrete LLC logo"
                                    className="w-full h-full object-contain logo-glow logo-float"
                                />
                            </div>

                            {/* Corner tags */}
                            <div className="absolute -top-4 -right-4 bg-yellow-400 text-black px-3 py-2 font-mono-accent text-[10px]">
                                WARRANTY · INSURED
                            </div>
                            <div className="absolute -bottom-4 -left-4 bg-black border border-zinc-700 text-white px-3 py-2 font-mono-accent text-[10px]">
                                EST. 2019 · CO
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats bar */}
                <div className="mt-16 md:mt-24 grid grid-cols-3 border-t border-b border-zinc-900 divide-x divide-zinc-900">
                    {hero.stats?.map((s, i) => (
                        <div
                            key={i}
                            data-testid={`hero-stat-${i}`}
                            className="py-8 md:py-10 px-4 text-center hover:bg-yellow-400/5 transition-colors"
                        >
                            <div className="font-display text-4xl md:text-6xl text-yellow-400">
                                {s.value}
                            </div>
                            <div className="font-mono-accent text-[10px] md:text-[11px] text-zinc-400 mt-2">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
